/**
 * UNIFIED LOCATION VALIDATION MIDDLEWARE
 *
 * Validates location routes in a root-agnostic way: works for property types
 * (hostels, hotels, bed-and-breakfast) and for other roots (e.g. events).
 * After validation, location data is stored via useState('locationData')
 * so the page can consume it via useLocationData().
 *
 * Usage in definePageMeta:
 *   validateLocation: {
 *     level: 'continent' | 'country' | 'city' | 'region' | 'area' | 'district',
 *     rootKind: 'property' | 'internal',
 *     // For rootKind 'internal' only:
 *     rootSlugKey: 't_OGEVENTSSLUG',  // i18n key for the root segment slug
 *     apiKey: 'hostel',               // key for location APIs that require it
 *   }
 *
 * Property routes: [propertyType]/[continent]/[country]/[city]/...
 * Internal routes: e.g. events/[continent]/[country]/[city]/...
 *
 * Server vs client behaviour:
 *   - API calls run on both server and client. Client-side results are served
 *     from the composable cache, so there is no extra network overhead on
 *     SPA navigation.
 *   - 404 for unresolved locations runs on server and client after clearing
 *     useState('locationData'). Negative caching of 404s remains server-only.
 *   - Canonical redirects (301 on server; client navigateTo with replace)
 *     run when the request path differs from the composable pathname (case,
 *     trailing slash, or slug resolved via APIs).
 *   - Location state is written via useState() so it is reactive and remains
 *     correct across both the initial SSR render and subsequent client
 *     navigations (e.g. clicking "other cities" links on a city page).
 */

import { useApiAreas } from '~/composables/useApiAreas';
import { useApiCities } from '~/composables/useApiCities';
import { useApiContinents } from '~/composables/useApiContinents';
import { useApiCountries } from '~/composables/useApiCountries';
import { useApiDistricts } from '~/composables/useApiDistricts';
import { useApiRegions } from '~/composables/useApiRegions';
import { useRoutes } from '~/composables/useRoutes.js';
import { useUrls } from '~/composables/useUrls';

/**
 * Nuxt sets false at build time; in Vitest it is undefined (falsy).
 * When process.env.VITEST is set, tests may assign globalThis.__HW_TEST_IS_NUXT_SERVER__
 * to exercise server-only branches (404, canonical redirects).
 */
function isNuxtServer() {
  if (
    typeof process !== 'undefined'
    && process.env.VITEST
    && typeof globalThis.__HW_TEST_IS_NUXT_SERVER__ === 'boolean'
  ) {
    return globalThis.__HW_TEST_IS_NUXT_SERVER__;
  }
  return Boolean(false);
}

const LOCATION_LEVELS = ['continent', 'country', 'city', 'region', 'area', 'district'];

// Maps each location level to a function that extracts the URL-friendly fields
// from a validated location object. Defined at module level — not recreated per request.
const LEVEL_URL_DATA = {
  continent: (loc) => ({ urlFriendlyContinent: loc.urlFriendlyName }),
  country: (loc) => ({
    urlFriendlyContinent: loc.urlFriendlyContinent,
    urlFriendlyCountry: loc.urlFriendlyName,
  }),
  city: (loc) => ({
    urlFriendlyContinent: loc.urlFriendlyContinent,
    urlFriendlyCountry: loc.urlFriendlyCountry,
    urlFriendlyCity: loc.urlFriendlyName,
  }),
  region: (loc) => ({
    urlFriendlyContinent: loc.urlFriendlyContinent,
    urlFriendlyCountry: loc.urlFriendlyCountry,
    urlFriendlyRegion: loc.urlFriendlyName,
  }),
  area: (loc) => ({
    urlFriendlyContinent: loc.urlFriendlyContinent,
    urlFriendlyCountry: loc.urlFriendlyCountry,
    urlFriendlyArea: loc.urlFriendlyName,
  }),
  district: (loc) => ({
    urlFriendlyContinent: loc.urlFriendlyContinent,
    urlFriendlyCountry: loc.urlFriendlyCountry,
    urlFriendlyCity: loc.urlFriendlyCity,
    urlFriendlyDistrict: loc.urlFriendlyName,
  }),
};

// Maps root kind + level to the useUrl method name that builds the canonical URL.
const CANONICAL_URL_BUILDERS = {
  property: {
    continent: 'getContinentPageUrl',
    country: 'getCountryPageUrl',
    city: 'getCityPageUrl',
    region: 'getRegionPageUrl',
    area: 'getAreaPageUrl',
    district: 'getDistrictPageUrl',
  },
  internal: {
    continent: 'getEventsContinentPageUrl',
    country: 'getEventsCountryPageUrl',
    city: 'getEventsCityPageUrl',
  },
};

const negativeLocationCache = new Map();
const NEGATIVE_CACHE_TTL = 300000; // 5 mins
const MAX_NEGATIVE_CACHE_ENTRIES = 5000;

/**
 * Stable key for negative 404 cache: lowercase and strip a trailing slash so
 * /Foo, /foo, and /foo/ map to the same entry (bots and external links).
 */
function normalisePathForNegativeCacheKey(path) {
  let p = String(path || '').toLowerCase();
  if (p.length > 1 && p.endsWith('/')) {
    p = p.slice(0, -1);
  }
  return p;
}

export default defineNuxtRouteMiddleware(async (to) => {
  const nuxtApp = useNuxtApp();

  // On server, bail out if there is already a payload error being handled.
  if (isNuxtServer() && nuxtApp.payload.error) {
    return;
  }

  const meta = to.meta?.validateLocation;
  if (!meta?.level || !LOCATION_LEVELS.includes(meta.level)) {
    return;
  }

  const {
    level,
    rootKind,
    rootSlugKey,
    apiKey,
  } = meta;
  const urlParams = to?.params || {};
  const urlQuery = to?.query || {};
  const requestPath = to?.path || '';
  const urlPathLower = requestPath.toLowerCase();
  const negativeCachePathKey = normalisePathForNegativeCacheKey(requestPath);

  const { useRouteManager } = useRoutes();
  const { useUrl } = useUrls();

  let root;
  let locationObj = null;
  let continentObj = null;

  if (rootKind === 'property') {
    const propertyTypeObj = useRouteManager.validatePropertyTypeParam(urlParams.propertyType);
    if (!propertyTypeObj?.key || propertyTypeObj.key === '404') {
      return;
    }
    root = {
      ...propertyTypeObj,
      kind: 'property',
    };
  } else if (rootKind === 'internal') {
    // useI18n() is only valid in component setup; route middleware must use the app i18n instance.
    const rootSlug = rootSlugKey && typeof nuxtApp.$i18n?.t === 'function' ? nuxtApp.$i18n.t(rootSlugKey) : '';
    const resolvedApiKey = apiKey || 'hostel';
    root = {
      kind: 'internal',
      slug: rootSlug,
      apiKey: resolvedApiKey,
    };
  } else {
    return;
  }

  const apiKeyForLocation = root.kind === 'property' ? root.key : root.apiKey;
  const negCacheKey = `${apiKeyForLocation}:${level}:${negativeCachePathKey}`;

  // Evaluate Negative Caching Server-Side
  if (isNuxtServer()) {
    const expiry = negativeLocationCache.get(negCacheKey);
    if (expiry) {
      if (Date.now() < expiry) {
        // Refresh LRU order
        negativeLocationCache.delete(negCacheKey);
        negativeLocationCache.set(negCacheKey, expiry);

        throw createError({
          statusCode: 404,
          statusMessage: 'Invalid location path (cached)',
        });
      } else {
        negativeLocationCache.delete(negCacheKey);
      }
    }
  }

  // API calls run on both server and client. On the client, composable-level
  // caching means these return immediately when the data was already fetched
  // during SSR, so there is no extra network request on SPA navigation.
  if (level === 'continent') {
    const { getContinentByName } = useApiContinents();
    locationObj = await getContinentByName(apiKeyForLocation, urlParams.continent);
  } else if (level === 'country') {
    const { getContinentByName } = useApiContinents();
    const { getCountryByName } = useApiCountries();
    [continentObj, locationObj] = await Promise.all([
      getContinentByName(apiKeyForLocation, urlParams.continent),
      getCountryByName(apiKeyForLocation, urlParams.country),
    ]);
    if (!continentObj?.id || !locationObj?.id) {
      locationObj = null;
    }
  } else if (level === 'city') {
    const { getCityByCityNameAndCountryName } = useApiCities();
    locationObj = await getCityByCityNameAndCountryName(urlParams.country, urlParams.city);
  } else if (level === 'region') {
    const { getRegionByName } = useApiRegions();
    locationObj = await getRegionByName(apiKeyForLocation, urlParams.country, urlParams.region);
  } else if (level === 'area') {
    const { getAreaByName } = useApiAreas();
    locationObj = await getAreaByName(apiKeyForLocation, urlParams.country, urlParams.area);
  } else if (level === 'district') {
    const { getDistrictByCityNameAndCountryName } = useApiDistricts();
    locationObj = await getDistrictByCityNameAndCountryName(
      apiKeyForLocation,
      urlParams.country,
      urlParams.city,
      urlParams.district,
    );
  }

  if (!locationObj?.id) {
    useState('locationData').value = null;

    if (isNuxtServer()) {
      if (negativeLocationCache.size >= MAX_NEGATIVE_CACHE_ENTRIES) {
        const firstKey = negativeLocationCache.keys().next().value;
        if (firstKey)
          negativeLocationCache.delete(firstKey);
      }
      negativeLocationCache.set(negCacheKey, Date.now() + NEGATIVE_CACHE_TTL);
    }

    const sanitize = (str) => String(str || '').replace(/[<>"\r\n]/g, '');
    const sContinent = sanitize(urlParams.continent);
    const sCountry = sanitize(urlParams.country);
    const sCity = sanitize(urlParams.city);
    const sRegion = sanitize(urlParams.region);
    const sArea = sanitize(urlParams.area);
    const sDistrict = sanitize(urlParams.district);

    const isCountryMissingContinent = level === 'country' && !continentObj?.id;
    const errorMessages = {
      continent: `### No such Continent '${sContinent}'`,
      country: isCountryMissingContinent
        ? `### No such Continent '${sContinent}'`
        : `### No such Country '${sCountry}' in Continent '${sContinent}'`,
      city: `### No such City '${sCity}' in Country '${sCountry}'`,
      region: `### No such Region '${sRegion}' in Country '${sCountry}'`,
      area: `### No such Area '${sArea}' in Country '${sCountry}'`,
      district: `### No such District '${sDistrict}' in City '${sCity}' from Country '${sCountry}'`,
    };
    const errorDescription = errorMessages[level] || '### Invalid location';
    console.error(errorDescription);

    let urlForTranslation = urlPathLower;
    if (level === 'country' && continentObj?.id && !locationObj?.id) {
      urlForTranslation = urlPathLower.replace(`${String(urlParams.continent || '').toLowerCase()}/`, '');
    }

    throw createError({
      statusCode: 404,
      statusMessage: errorDescription.replace(/^### /, ''),
      data: useRouteManager.enrichedErrorResponse(errorDescription, urlForTranslation),
    });
  }

  const base = root.kind === 'property' ? { propertyTypeSlug: root.slug } : { rootSlug: root.slug };
  const locationDataForUrl = {
    ...base,
    ...(LEVEL_URL_DATA[level]?.(locationObj) || {}),
  };

  const urlBuilderName = CANONICAL_URL_BUILDERS[root.kind]?.[level];
  const canonicalUrl = urlBuilderName ? useUrl[urlBuilderName](locationDataForUrl, urlQuery) : null;

  if (canonicalUrl) {
    let canonicalPathname;
    try {
      canonicalPathname = new URL(canonicalUrl, 'https://_').pathname;
    } catch {
      canonicalPathname = '';
    }
    if (canonicalPathname && requestPath !== canonicalPathname) {
      return navigateTo(canonicalUrl, {
        redirectCode: isNuxtServer() ? 301 : undefined,
        external: true,
        ...(!isNuxtServer() ? { replace: true } : {}),
      });
    }
  }

  // Always set so client navigations to an invalid location do not leave stale data
  // from the previous route in useState('locationData').
  useState('locationData').value = locationObj?.id
    ? {
      rootKind: root.kind,
      root,
      level,
      location: locationObj,
      // continentObj is only fetched at the country level; expose it so country
      // pages can access parent-continent data without a second API call.
      ...(continentObj ? { parentContinent: continentObj } : {}),
    }
    : null;
});
